// src/pages/GalleriesPhotos/AlbumGaleriePage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useScrollToTop from "../../hooks/useScrollToTop";
import { getGalleryById } from "../../services/gallerieServices";
import Button from "../../components/Button/Button";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import {
  FaPlus,
  FaMinus,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
} from "react-icons/fa";
import AddPhotoToAlbumModal from "./AddPhotoToAlbumModal";
import RemovePhotoFromAlbumModal from "./RemovePhotoFromAlbumModal";
import { addPhoto } from "../../services/photoServices";
import { ToastContainer, toast } from "react-toastify";

const AlbumGaleriePage = () => {
  useScrollToTop();

  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [gallery, setGallery] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [isAddPhotoModalOpen, setIsAddPhotoModalOpen] = useState(false);
  const [isRemovePhotoModalOpen, setIsRemovePhotoModalOpen] = useState(false);
  const [newPhotoId, setNewPhotoId] = useState(null);

  const isAdmin = userRole === "Administrateur" || userRole === "Bureau";

  const fetchGallery = async () => {
    try {
      const response = await getGalleryById(Number(id));
      if (response && response.id) {
        setGallery(response);
      } else {
        setGallery(null);
      }
    } catch (error) {
      console.error("Erreur lors du chargement de la galerie:", error);
      toast.error("Erreur lors du chargement de la galerie.");
      setGallery(null);
    }
  };

  useEffect(() => {
    fetchGallery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Gestion lightbox clavier
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKeyDown = (e) => {
      if (!gallery || !gallery.photos || gallery.photos.length === 0) return;
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight") {
        setLightboxIndex((prev) =>
          prev === null
            ? 0
            : (prev + 1 + gallery.photos.length) % gallery.photos.length
        );
      }
      if (e.key === "ArrowLeft") {
        setLightboxIndex((prev) =>
          prev === null
            ? 0
            : (prev - 1 + gallery.photos.length) % gallery.photos.length
        );
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, gallery]);

  if (!gallery)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 dark:text-white">
        Album introuvable
      </div>
    );

  const handleAddPhotoClick = async () => {
    try {
      const newPhoto = await addPhoto({ gallerie_id: id, is_active: true });
      setNewPhotoId(newPhoto.id);
      setIsAddPhotoModalOpen(true);
    } catch (error) {
      console.error("Erreur lors de l'ajout de la photo:", error);
      toast.error("Erreur lors de la création de la photo.");
    }
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  const showNext = (e) => {
    e.stopPropagation();
    if (!gallery.photos || gallery.photos.length === 0) return;
    setLightboxIndex((prev) =>
      prev === null
        ? 0
        : (prev + 1 + gallery.photos.length) % gallery.photos.length
    );
  };

  const showPrev = (e) => {
    e.stopPropagation();
    if (!gallery.photos || gallery.photos.length === 0) return;
    setLightboxIndex((prev) =>
      prev === null
        ? 0
        : (prev - 1 + gallery.photos.length) % gallery.photos.length
    );
  };

  const photos = gallery.photos || [];

  return (
    <div className="min-h-screen flex flex-col w-full mx-auto pt-[130px] sm:pt-[130px] md:pt-[150px] lg:pt-[150px] xl:pt-[150px] pb-10 bg-slate-50 dark:bg-slate-900 dark:text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-4 md:mb-6">
          <p className="text-[11px] font-semibold tracking-[0.35em] uppercase text-primary mb-1">
            Visiteurs & Utilisateurs
          </p>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight font-montserrat uppercase">
            Photos
          </h1>
        </div>

        <div className="mb-4 flex justify-between items-center gap-3">
          <Button
            text="Retour"
            onClick={() => navigate(-1)}
            className="text-sm"
          />
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300">
            {photos.length} photo
            {photos.length > 1 && "s"} dans cet album.
          </p>
        </div>

        {/* Infos album */}
        <div className="rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 shadow-lg mb-6 p-5">
          {gallery.category && (
            <p className="text-[11px] uppercase tracking-[0.25em] text-primary mb-2">
              {gallery.category}
            </p>
          )}
          <h2 className="text-xl md:text-2xl font-bold mb-3">
            {gallery.titre}
          </h2>
          <div
            className="text-sm text-slate-700 dark:text-slate-200 italic mb-3"
            dangerouslySetInnerHTML={{ __html: gallery.description }}
          />
        </div>

        {/* Grid des photos */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {photos.map((photo, index) => (
            <div
              key={photo.id || index}
              className="group relative bg-white dark:bg-slate-900 rounded-xl shadow-md border border-slate-200/70 dark:border-slate-700/70 overflow-hidden cursor-pointer"
              onClick={() => openLightbox(index)}
            >
              <div className="overflow-hidden">
                <img
                  src={photo.photo_url}
                  alt={`Photo ${index + 1}`}
                  className="object-cover w-full h-48 transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="p-3">
                <p className="text-[12px] text-slate-700 dark:text-slate-200 italic line-clamp-3">
                  {photo.description}
                </p>
              </div>
            </div>
          ))}

          {isAdmin && (
            <>
              <div
                onClick={handleAddPhotoClick}
                className="cursor-pointer bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-slate-700 dark:via-slate-800 dark:to-slate-900 border-2 border-dashed border-primary/60 dark:border-primary/70 rounded-xl p-4 text-center flex flex-col items-center justify-center h-48 shadow-md hover:shadow-lg transition-all duration-200"
              >
                <FaPlus className="text-4xl mb-3 bg-primary text-white rounded-full p-3" />
                <p className="text-sm font-semibold">
                  Ajouter une photo à cet album
                </p>
              </div>

              <div
                onClick={() => setIsRemovePhotoModalOpen(true)}
                className="cursor-pointer bg-gradient-to-br from-rose-500/10 via-rose-500/5 to-transparent dark:from-slate-700 dark:via-slate-800 dark:to-slate-900 border-2 border-dashed border-rose-400 dark:border-rose-500 rounded-xl p-4 text-center flex flex-col items-center justify-center h-48 shadow-md hover:shadow-lg transition-all duration-200"
              >
                <FaMinus className="text-4xl mb-3 bg-rose-500 text-white rounded-full p-3" />
                <p className="text-sm font-semibold">
                  Retirer une photo de cet album
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && photos[lightboxIndex] && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <div
            className="relative max-w-5xl w-[95%] max-h-[90vh] bg-slate-900/90 rounded-2xl overflow-hidden border border-slate-700 shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700">
              <p className="text-xs text-slate-300">
                Photo {lightboxIndex + 1} / {photos.length}
              </p>
              <button
                onClick={closeLightbox}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/50 text-white hover:bg-black/80"
              >
                <FaTimes />
              </button>
            </div>

            {/* Image + navigation */}
            <div className="flex-1 flex items-center justify-center relative px-10 py-4">
              <button
                className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/60 hover:bg-black/90 text-white"
                onClick={showPrev}
              >
                <FaChevronLeft />
              </button>
              <img
                src={photos[lightboxIndex].photo_url}
                alt={`Photo ${lightboxIndex + 1}`}
                className="max-w-full max-h-[65vh] object-contain rounded-lg"
              />
              <button
                className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/60 hover:bg-black/90 text-white"
                onClick={showNext}
              >
                <FaChevronRight />
              </button>
            </div>

            {/* Description */}
            {photos[lightboxIndex].description && (
              <div className="px-5 pb-4 text-xs sm:text-sm text-slate-200 italic">
                {photos[lightboxIndex].description}
              </div>
            )}
          </div>
        </div>
      )}

      {isAddPhotoModalOpen && (
        <AddPhotoToAlbumModal
          isOpen={isAddPhotoModalOpen}
          onClose={() => {
            setIsAddPhotoModalOpen(false);
            fetchGallery();
          }}
          galleryId={id}
          photoId={newPhotoId}
        />
      )}

      {isRemovePhotoModalOpen && (
        <RemovePhotoFromAlbumModal
          isOpen={isRemovePhotoModalOpen}
          onClose={() => {
            setIsRemovePhotoModalOpen(false);
            fetchGallery();
          }}
          galleryId={id}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default AlbumGaleriePage;
